import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
  Users,
  Mail,
  Calendar,
  Download,
  Search,
  Filter,
  LogOut,
  AlertCircle,
  TrendingUp,
  Eye,
  Trash2,
  RefreshCw,
  FileText,
  Plus,
  Edit,
  Save,
  X,
  LayoutDashboard,
  Bot,
  Zap
} from "lucide-react";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeDashboard, setActiveDashboard] = useState("website");
  const [activeTab, setActiveTab] = useState("contacts");
  const [isLoading, setIsLoading] = useState(true);
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [pageSourceFilter, setPageSourceFilter] = useState("");
  const [selectedContact, setSelectedContact] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    thisMonth: 0,
    thisWeek: 0,
    topSources: []
  });

  // White Paper Management State
  const [whitepapers, setWhitepapers] = useState([]);
  const [isWhitepapersLoading, setIsWhitepapersLoading] = useState(false);
  const [showAddWhitepaper, setShowAddWhitepaper] = useState(false);
  const [editingWhitepaper, setEditingWhitepaper] = useState(null);
  const [whitepaper, setWhitepaper] = useState({
    number: "",
    title_de: "",
    title_en: "",
    pdf_url: ""
  });

  // Siteaudit State
  const [auditUrl, setAuditUrl] = useState("");
  const [auditId, setAuditId] = useState(null);
  const [auditStatus, setAuditStatus] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [auditReportFile, setAuditReportFile] = useState(null);
  const [auditReports, setAuditReports] = useState([]);
  const [isAuditLoading, setIsAuditLoading] = useState(false);
  const [isAuditStarting, setIsAuditStarting] = useState(false);

  // Check authentication
  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    const user = localStorage.getItem("admin_user");

    if (!token || !user) {
      navigate("/admin/login");
      return;
    }

    fetchContacts();
    if (activeTab === "whitepapers") {
      fetchWhitepapers();
    }
  }, [navigate, activeTab]);

  // Fetch contacts from backend
  const fetchContacts = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const response = await fetch(`${import.meta.env?.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL}/api/admin/contacts`, {
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setContacts(data.contacts);
        setFilteredContacts(data.contacts);
        calculateStats(data.contacts);
      } else if (response.status === 401) {
        handleLogout();
      }
    } catch (error) {
      // Admin error handled silently
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate statistics
  const calculateStats = (contactsData) => {
    const now = new Date();
    const oneMonthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

    const thisMonth = contactsData.filter(contact =>
      new Date(contact.created_at) >= oneMonthAgo
    ).length;

    const thisWeek = contactsData.filter(contact =>
      new Date(contact.created_at) >= oneWeekAgo
    ).length;

    // Count by page source
    const sourceCounts = {};
    contactsData.forEach(contact => {
      sourceCounts[contact.pageSource] = (sourceCounts[contact.pageSource] || 0) + 1;
    });

    const topSources = Object.entries(sourceCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5)
      .map(([source, count]) => ({ source, count }));

    setStats({
      total: contactsData.length,
      thisMonth,
      thisWeek,
      topSources
    });
  };

  // Filter contacts based on search and filters
  useEffect(() => {
    let filtered = [...contacts];

    if (searchTerm) {
      filtered = filtered.filter(contact =>
        contact.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contact.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contact.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (contact.message && contact.message.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    if (dateFilter) {
      const filterDate = new Date(dateFilter);
      filtered = filtered.filter(contact => {
        const contactDate = new Date(contact.created_at);
        return contactDate.toDateString() === filterDate.toDateString();
      });
    }

    if (pageSourceFilter) {
      filtered = filtered.filter(contact =>
        contact.pageSource === pageSourceFilter
      );
    }

    setFilteredContacts(filtered);
  }, [contacts, searchTerm, dateFilter, pageSourceFilter]);

  // Format date to Berlin timezone
  const formatBerlinTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("de-DE", {
      timeZone: "Europe/Berlin",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    });
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_user");
    navigate("/admin/login");
  };

  // White Paper Management Functions
  const fetchWhitepapers = async () => {
    setIsWhitepapersLoading(true);
    try {
      const token = localStorage.getItem("admin_token");
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/whitepapers`, {
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setWhitepapers(data.whitepapers || []);
      }
    } catch (error) {
      // Error handled silently
    }
    setIsWhitepapersLoading(false);
  };

  const handleSubmitWhitepaper = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("admin_token");
      const url = editingWhitepaper
        ? `${process.env.REACT_APP_BACKEND_URL}/api/admin/whitepapers/${editingWhitepaper.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/admin/whitepapers`;

      const method = editingWhitepaper ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(whitepaper)
      });

      if (response.ok) {
        fetchWhitepapers();
        resetWhitepaperForm();
      }
    } catch (error) {
      // Error handled silently
    }
  };

  const handleDeleteWhitepaper = async (id) => {
    if (!window.confirm("White Paper wirklich löschen?")) return;

    try {
      const token = localStorage.getItem("admin_token");
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/whitepapers/${id}`, {
        method: 'DELETE',
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });

      if (response.ok) {
        fetchWhitepapers();
      }
    } catch (error) {
      // Error handled silently
    }
  };

  const handleEditWhitepaper = (wp) => {
    setEditingWhitepaper(wp);
    setWhitepaper({
      number: wp.number,
      title_de: wp.title_de,
      title_en: wp.title_en,
      pdf_url: wp.pdf_url
    });
    setShowAddWhitepaper(true);
  };

  const resetWhitepaperForm = () => {
    setShowAddWhitepaper(false);
    setEditingWhitepaper(null);
    setWhitepaper({
      number: "",
      title_de: "",
      title_en: "",
      pdf_url: ""
    });
  };

  // Siteaudit Functions
  const SITEAUDIT_API = "/api/siteaudit";

  const fetchAuditReports = async () => {
    try {
      setIsAuditLoading(true);
      const res = await fetch(`${SITEAUDIT_API}/reports`);
      if (res.ok) {
        const data = await res.json();
        setAuditReports(data || []);
      }
    } catch {
      // Silently handle
    } finally {
      setIsAuditLoading(false);
    }
  };

  const startAudit = async () => {
    if (!auditUrl.trim()) return;
    setIsAuditStarting(true);
    setAuditStatus(null);
    setAuditLog([]);
    setAuditReportFile(null);
    try {
      const res = await fetch(`${SITEAUDIT_API}/check`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: auditUrl.trim() })
      });
      if (res.ok) {
        const data = await res.json();
        setAuditId(data.id);
        setAuditStatus("running");
      } else {
        const err = await res.json().catch(() => ({}));
        setAuditStatus("error");
        setAuditLog([err.error || "Fehler beim Starten"]);
      }
    } catch {
      setAuditStatus("error");
      setAuditLog(["Verbindung zum Siteaudit-Service fehlgeschlagen"]);
    } finally {
      setIsAuditStarting(false);
    }
  };

  // Poll audit status
  useEffect(() => {
    if (!auditId || auditStatus !== "running") return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${SITEAUDIT_API}/check/${auditId}`);
        if (res.ok) {
          const data = await res.json();
          setAuditLog(data.log || []);
          if (data.status !== "running") {
            setAuditStatus(data.status);
            setAuditReportFile(data.reportFile || null);
            clearInterval(interval);
            fetchAuditReports();
          }
        }
      } catch {
        // Retry on next interval
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [auditId, auditStatus]);

  // Load reports when siteaudit tab is active
  useEffect(() => {
    if (activeDashboard === "siteaudit") {
      fetchAuditReports();
    }
  }, [activeDashboard]);

  // Export to CSV
  const exportToCSV = () => {
    const headers = ["Datum", "Name", "Email", "Telefon", "Nachricht", "Seite", "Button Kontext", "Button Text"];

    const csvData = filteredContacts.map(contact => [
      formatBerlinTime(contact.created_at),
      `${contact.firstName} ${contact.lastName}`,
      contact.email,
      contact.phone || "",
      contact.message || "",
      contact.pageSource,
      contact.buttonContext || "",
      contact.buttonText || ""
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `brovko-leads-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // Get unique page sources for filter
  const uniquePageSources = [...new Set(contacts.map(contact => contact.pageSource))];

  // Dark theme input class
  const inputClass = "w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent text-sm";

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="flex items-center gap-2 text-gray-300">
          <RefreshCw className="h-6 w-6 animate-spin text-sky-400" />
          <span>Dashboard wird geladen...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />
      </Helmet>
      <div className="min-h-screen bg-gray-950 text-gray-100">

      {/* Header */}
      <header className="bg-gray-900 border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-white">BROVKO Admin</h1>
              <p className="text-gray-400 text-sm">Lead Management & Analytics</p>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-400">
                {localStorage.getItem("admin_user")}
              </span>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-3 py-2 text-sm text-gray-400 hover:text-white border border-gray-700 rounded-lg hover:border-gray-600 transition-colors"
              >
                <LogOut className="h-4 w-4" />
                Abmelden
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Dashboard Selector */}
      <div className="bg-gray-900 border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-1 py-3">
            {[
              { id: "website", icon: LayoutDashboard, label: "Website Dashboard" },
              { id: "dashboard1", icon: Bot, label: "Dashboard 1" },
              { id: "dashboard2", icon: Zap, label: "Dashboard 2" },
              { id: "siteaudit", icon: Search, label: "Website-Check" },
            ].map(({ id, icon: Icon, label }) => (
              <button
                key={id}
                onClick={() => setActiveDashboard(id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeDashboard === id
                    ? "bg-sky-500 text-white"
                    : "bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-gray-200"
                }`}
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* ========== WEBSITE DASHBOARD ========== */}
        {activeDashboard === "website" && (
          <>
            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-400">Gesamt Kontakte</p>
                    <p className="text-3xl font-bold text-white">{stats.total}</p>
                  </div>
                  <Users className="h-8 w-8 text-sky-400" />
                </div>
              </div>
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-400">Diesen Monat</p>
                    <p className="text-3xl font-bold text-white">{stats.thisMonth}</p>
                  </div>
                  <Calendar className="h-8 w-8 text-sky-400" />
                </div>
              </div>
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-400">Diese Woche</p>
                    <p className="text-3xl font-bold text-white">{stats.thisWeek}</p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-green-400" />
                </div>
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="mb-8">
              <div className="border-b border-gray-800">
                <nav className="-mb-px flex space-x-8">
                  <button
                    onClick={() => setActiveTab("contacts")}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === "contacts"
                        ? "border-sky-500 text-sky-400"
                        : "border-transparent text-gray-500 hover:text-gray-300 hover:border-gray-600"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Kontakte verwalten
                    </div>
                  </button>
                  <button
                    onClick={() => setActiveTab("whitepapers")}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === "whitepapers"
                        ? "border-sky-500 text-sky-400"
                        : "border-transparent text-gray-500 hover:text-gray-300 hover:border-gray-600"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      White Papers verwalten
                    </div>
                  </button>
                </nav>
              </div>
            </div>

            {/* Top Sources */}
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 mb-8">
              <h3 className="text-lg font-semibold text-white mb-4">Top Herkunftsseiten</h3>
              <div className="space-y-3">
                {stats.topSources.map((source) => (
                  <div key={source.source} className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-300">{source.source}</span>
                    <span className="bg-sky-500/20 text-sky-400 rounded-full px-3 py-1 text-sm font-semibold">{source.count}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Contacts Tab */}
            {activeTab === "contacts" && (
              <>
                {/* Filters */}
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 mb-6">
                  <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
                    <div className="flex flex-col sm:flex-row gap-4 flex-1">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
                        <input
                          type="text"
                          placeholder="Suchen..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className={`${inputClass} pl-10 sm:w-64`}
                        />
                      </div>
                      <input
                        type="date"
                        value={dateFilter}
                        onChange={(e) => setDateFilter(e.target.value)}
                        className={`${inputClass} sm:w-auto`}
                      />
                      <select
                        value={pageSourceFilter}
                        onChange={(e) => setPageSourceFilter(e.target.value)}
                        className={`${inputClass} sm:w-auto`}
                      >
                        <option value="">Alle Seiten</option>
                        {uniquePageSources.map(source => (
                          <option key={source} value={source}>{source}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => { setSearchTerm(""); setDateFilter(""); setPageSourceFilter(""); }}
                        className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-400 border border-gray-700 rounded-xl hover:border-gray-600 hover:text-gray-200 transition-colors"
                      >
                        <Filter className="h-4 w-4" />
                        Zurücksetzen
                      </button>
                      <button
                        onClick={exportToCSV}
                        className="flex items-center gap-2 px-4 py-2.5 text-sm bg-sky-500 hover:bg-sky-600 text-white font-semibold rounded-xl transition-colors"
                      >
                        <Download className="h-4 w-4" />
                        Export CSV
                      </button>
                    </div>
                  </div>
                </div>

                {/* Contacts Table */}
                <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-800">
                    <h3 className="text-lg font-semibold text-white">Kontaktanfragen ({filteredContacts.length})</h3>
                  </div>
                  {filteredContacts.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <AlertCircle className="h-12 w-12 mx-auto mb-4 text-gray-600" />
                      <p>Keine Kontaktanfragen gefunden.</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-gray-800">
                            <th className="text-left py-3 px-6 font-medium text-gray-400 text-sm">Datum</th>
                            <th className="text-left py-3 px-6 font-medium text-gray-400 text-sm">Name</th>
                            <th className="text-left py-3 px-6 font-medium text-gray-400 text-sm">Email</th>
                            <th className="text-left py-3 px-6 font-medium text-gray-400 text-sm">Seite</th>
                            <th className="text-left py-3 px-6 font-medium text-gray-400 text-sm">Aktionen</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredContacts.map((contact) => (
                            <tr key={contact.id} className="border-b border-gray-800/60 hover:bg-gray-800/40 transition-colors">
                              <td className="py-3 px-6 text-sm text-gray-300">
                                {formatBerlinTime(contact.created_at)}
                              </td>
                              <td className="py-3 px-6 text-sm font-medium text-white">
                                {contact.firstName} {contact.lastName}
                              </td>
                              <td className="py-3 px-6 text-sm">
                                <a href={`mailto:${contact.email}`} className="text-sky-400 hover:underline">
                                  {contact.email}
                                </a>
                              </td>
                              <td className="py-3 px-6 text-sm text-gray-400">
                                {contact.pageSource}
                              </td>
                              <td className="py-3 px-6">
                                <button
                                  onClick={() => setSelectedContact(contact)}
                                  className="flex items-center gap-1 px-3 py-1.5 text-xs text-gray-400 border border-gray-700 rounded-lg hover:border-sky-500/50 hover:text-sky-400 transition-colors"
                                >
                                  <Eye className="h-3.5 w-3.5" />
                                  Details
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Contact Detail Modal */}
                {selectedContact && (
                  <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
                    <div className="bg-gray-900 border border-gray-800 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                      <div className="px-6 py-4 border-b border-gray-800">
                        <h3 className="text-lg font-semibold text-white">Kontakt Details</h3>
                      </div>
                      <div className="p-6 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium text-gray-500">Name</label>
                            <p className="text-white">{selectedContact.firstName} {selectedContact.lastName}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-500">Email</label>
                            <p className="text-sky-400">{selectedContact.email}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-500">Telefon</label>
                            <p className="text-white">{selectedContact.phone || "Nicht angegeben"}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-500">Datum</label>
                            <p className="text-white">{formatBerlinTime(selectedContact.created_at)}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-500">Herkunftsseite</label>
                            <p className="text-white">{selectedContact.pageSource}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-500">Button Kontext</label>
                            <p className="text-white">{selectedContact.buttonContext || "Nicht verfügbar"}</p>
                          </div>
                        </div>

                        {selectedContact.message && (
                          <div>
                            <label className="text-sm font-medium text-gray-500">Nachricht</label>
                            <div className="mt-1 p-3 bg-gray-800 rounded-lg">
                              <p className="text-gray-200 whitespace-pre-wrap">{selectedContact.message}</p>
                            </div>
                          </div>
                        )}

                        <div className="flex justify-end gap-2 pt-4">
                          <button
                            onClick={() => setSelectedContact(null)}
                            className="px-4 py-2 text-sm text-gray-400 border border-gray-700 rounded-xl hover:border-gray-600 transition-colors"
                          >
                            Schließen
                          </button>
                          <button
                            onClick={() => window.open(`mailto:${selectedContact.email}`, '_blank')}
                            className="flex items-center gap-2 px-4 py-2 text-sm bg-sky-500 hover:bg-sky-600 text-white font-semibold rounded-xl transition-colors"
                          >
                            <Mail className="h-4 w-4" />
                            Email senden
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* White Papers Tab */}
            {activeTab === "whitepapers" && (
              <>
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-white">White Papers verwalten</h2>
                    <p className="text-gray-400">Hinzufügen, bearbeiten und verwalten</p>
                  </div>
                  <button
                    onClick={() => setShowAddWhitepaper(true)}
                    className="flex items-center gap-2 px-4 py-2.5 text-sm bg-sky-500 hover:bg-sky-600 text-white font-semibold rounded-xl transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                    Neues White Paper
                  </button>
                </div>

                {/* Add/Edit Form */}
                {showAddWhitepaper && (
                  <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 mb-6">
                    <h3 className="text-lg font-semibold text-white mb-4">
                      {editingWhitepaper ? "White Paper bearbeiten" : "Neues White Paper hinzufügen"}
                    </h3>
                    <form onSubmit={handleSubmitWhitepaper} className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-400 mb-2">Nummer</label>
                          <input
                            type="text"
                            value={whitepaper.number}
                            onChange={(e) => setWhitepaper({...whitepaper, number: e.target.value})}
                            placeholder="#37 09/2025"
                            required
                            className={inputClass}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-400 mb-2">PDF URL</label>
                          <input
                            type="url"
                            value={whitepaper.pdf_url}
                            onChange={(e) => setWhitepaper({...whitepaper, pdf_url: e.target.value})}
                            placeholder="https://example.com/whitepaper.pdf"
                            required
                            className={inputClass}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-2">Titel (Deutsch)</label>
                        <input
                          type="text"
                          value={whitepaper.title_de}
                          onChange={(e) => setWhitepaper({...whitepaper, title_de: e.target.value})}
                          placeholder="Deutscher Titel des White Papers"
                          required
                          className={inputClass}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-2">Titel (Englisch)</label>
                        <input
                          type="text"
                          value={whitepaper.title_en}
                          onChange={(e) => setWhitepaper({...whitepaper, title_en: e.target.value})}
                          placeholder="English title of the white paper"
                          required
                          className={inputClass}
                        />
                      </div>
                      <div className="flex gap-2">
                        <button type="submit" className="flex items-center gap-2 px-4 py-2.5 text-sm bg-sky-500 hover:bg-sky-600 text-white font-semibold rounded-xl transition-colors">
                          <Save className="h-4 w-4" />
                          {editingWhitepaper ? "Aktualisieren" : "Hinzufügen"}
                        </button>
                        <button type="button" onClick={resetWhitepaperForm} className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-400 border border-gray-700 rounded-xl hover:border-gray-600 transition-colors">
                          <X className="h-4 w-4" />
                          Abbrechen
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {/* White Papers List */}
                <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-800 flex items-center gap-2">
                    <FileText className="h-5 w-5 text-gray-400" />
                    <h3 className="text-lg font-semibold text-white">Alle White Papers ({whitepapers.length})</h3>
                  </div>
                  <div className="p-6">
                    {isWhitepapersLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <RefreshCw className="h-6 w-6 animate-spin text-sky-400 mr-2" />
                        <span className="text-gray-400">White Papers werden geladen...</span>
                      </div>
                    ) : whitepapers.length === 0 ? (
                      <div className="text-center py-8">
                        <FileText className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-400">Keine White Papers vorhanden</h3>
                        <p className="text-gray-500">Fügen Sie Ihr erstes White Paper hinzu.</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {whitepapers.map((wp) => (
                          <div key={wp.id} className="bg-gray-800/50 border border-gray-800 rounded-xl p-4 hover:border-gray-700 transition-colors">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <span className="bg-sky-500/20 text-sky-400 px-3 py-1 rounded-full text-sm font-medium">{wp.number}</span>
                                  <div className="flex items-center gap-2 text-sm text-gray-500">
                                    <Calendar className="h-4 w-4" />
                                    {new Date(wp.created_at).toLocaleDateString("de-DE")}
                                  </div>
                                </div>
                                <h3 className="font-medium text-white mb-1">{wp.title_de}</h3>
                                <p className="text-sm text-gray-400 mb-2">{wp.title_en}</p>
                                <a href={wp.pdf_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-sky-400 hover:underline">
                                  <Eye className="h-4 w-4" />
                                  PDF ansehen
                                </a>
                              </div>
                              <div className="flex items-center gap-2 ml-4">
                                <button onClick={() => handleEditWhitepaper(wp)} className="p-2 text-gray-400 border border-gray-700 rounded-lg hover:border-gray-600 hover:text-white transition-colors">
                                  <Edit className="h-4 w-4" />
                                </button>
                                <button onClick={() => handleDeleteWhitepaper(wp.id)} className="p-2 text-red-400 border border-gray-700 rounded-lg hover:border-red-500/50 hover:text-red-300 transition-colors">
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {/* ========== DASHBOARD 1 ========== */}
        {activeDashboard === "dashboard1" && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-white">Dashboard 1</h2>
              <p className="text-gray-400 mt-1">Dieses Dashboard wird im Rahmen des email-agents Projekts befüllt.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-gray-900 border border-gray-800 rounded-xl p-6">
                  <div className="h-24 bg-gray-800 rounded-lg flex items-center justify-center">
                    <span className="text-gray-600 text-sm">Platzhalter {i}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-12 text-center">
              <Bot className="h-12 w-12 text-gray-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-400">Noch keine Daten</h3>
              <p className="text-gray-500 mt-1">Inhalte werden bald verfügbar sein.</p>
            </div>
          </div>
        )}

        {/* ========== DASHBOARD 2 ========== */}
        {activeDashboard === "dashboard2" && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-white">Dashboard 2</h2>
              <p className="text-gray-400 mt-1">Dieses Dashboard wird im Rahmen des email-agents Projekts befüllt.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-gray-900 border border-gray-800 rounded-xl p-6">
                  <div className="h-24 bg-gray-800 rounded-lg flex items-center justify-center">
                    <span className="text-gray-600 text-sm">Platzhalter {i}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-12 text-center">
              <Zap className="h-12 w-12 text-gray-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-400">Noch keine Daten</h3>
              <p className="text-gray-500 mt-1">Inhalte werden bald verfügbar sein.</p>
            </div>
          </div>
        )}

        {/* ========== SITEAUDIT ========== */}
        {activeDashboard === "siteaudit" && (
          <div className="max-w-5xl mx-auto">

            {/* Hero */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-sky-500 flex items-center justify-center text-sm font-bold text-white">AI</div>
                <span className="text-xl font-semibold">KI-Websitecheck</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold leading-tight mb-3">
                Deine Website.<br />
                <span className="text-sky-400">Vollautomatisch geprüft von KI.</span>
              </h2>
              <p className="text-gray-400 max-w-2xl">
                Kein manuelles Klicken, kein Rätselraten. Unser KI-Agent analysiert deine Website in 13 Schritten —
                gründlicher und schneller als jede manuelle Prüfung.
              </p>
            </div>

            {/* URL Input */}
            <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 max-w-2xl mb-10">
              <div className="flex gap-3">
                <input
                  type="url"
                  placeholder="https://deine-website.de"
                  value={auditUrl}
                  onChange={(e) => setAuditUrl(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && !isAuditStarting && auditStatus !== "running" && startAudit()}
                  disabled={auditStatus === "running"}
                  className="flex-1 bg-gray-800 border border-gray-700 rounded-xl px-5 py-3.5 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent text-lg disabled:opacity-50"
                />
                <button
                  onClick={startAudit}
                  disabled={isAuditStarting || auditStatus === "running" || !auditUrl.trim()}
                  className="bg-sky-500 hover:bg-sky-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold px-8 py-3.5 rounded-xl transition-colors text-lg whitespace-nowrap"
                >
                  {isAuditStarting || auditStatus === "running" ? "Läuft..." : "Check starten"}
                </button>
              </div>

              {auditStatus && (
                <div className="mt-4">
                  <div className="flex items-center gap-3 text-sm">
                    <div className={`w-2.5 h-2.5 rounded-full ${
                      auditStatus === "running" ? "bg-sky-400 animate-pulse" :
                      auditStatus === "done" ? "bg-green-400" : "bg-red-400"
                    }`} />
                    <span className="text-gray-400">
                      {auditStatus === "running" ? "Audit läuft — 13-Schritte-Analyse..." :
                       auditStatus === "done" ? "Audit abgeschlossen!" : "Audit fehlgeschlagen. Siehe Log."}
                    </span>
                  </div>

                  {auditLog.length > 0 && (
                    <div className="mt-3 bg-gray-800/50 rounded-lg p-3 max-h-40 overflow-y-auto text-xs text-gray-500 font-mono">
                      {auditLog.map((line, i) => (
                        <div key={i} className={line.startsWith("[stderr]") ? "text-yellow-400" : ""}>{line}</div>
                      ))}
                    </div>
                  )}

                  {auditStatus === "done" && auditReportFile && (
                    <div className="mt-4">
                      <button
                        onClick={() => window.open(`${SITEAUDIT_API}/ergebnisse/${auditReportFile}`, "_blank")}
                        className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-3 rounded-xl transition-colors"
                      >
                        <Eye className="h-4 w-4" />
                        Report ansehen
                      </button>
                    </div>
                  )}

                  {auditStatus === "done" && !auditReportFile && (
                    <p className="mt-3 text-yellow-400 text-sm">Kein Report-File gefunden. Prüfe die Logs.</p>
                  )}
                </div>
              )}
            </div>

            {/* 80-90% Highlight */}
            <div className="bg-gradient-to-r from-sky-900/30 to-gray-900 border border-sky-800/40 rounded-2xl p-8 flex items-center gap-8 mb-12">
              <div className="text-5xl md:text-6xl font-bold text-sky-400 whitespace-nowrap">80–90%</div>
              <div>
                <h3 className="text-lg font-semibold mb-1">der kritischen Website-Probleme aufgedeckt</h3>
                <p className="text-gray-400 text-sm">
                  Von technischen Fehlern über Sicherheitslücken bis zu SEO-Schwächen — ein einziger Audit deckt den Großteil aller Probleme auf, die deine Website bremsen.
                </p>
              </div>
            </div>

            {/* Was du bekommst */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold mb-2">Was du bekommst</h3>
              <p className="text-gray-400 mb-8">Jeder Audit liefert dir folgende Ergebnisse</p>
              <div className="grid md:grid-cols-2 gap-x-12 gap-y-1">
                {[
                  ["Vollständiger HTML-Report", "Score-Card mit Gesamtbewertung & Einzelkategorien"],
                  ["8 Kategorien analysiert", "Technik, Security, Recht, Navigation, Inhalt & mehr"],
                  ["Sicherheitslücken aufgedeckt", "Vergessene Credentials, offene Pfade, fehlende Header"],
                  ["Core Web Vitals & Performance", "LCP, CLS, INP, TTFB — die Google-Ranking-Faktoren"],
                  ["SEO On-Page Analyse", "Meta-Tags, Sitemaps, Schema.org, Canonical, Open Graph"],
                  ["KI-Sichtbarkeit (GEO)", "Wirst du in ChatGPT, Claude & Perplexity gefunden?"],
                  ["Priorisierte Empfehlungen", "Rot/Gelb/Grün mit Aufwandschätzung (Klein/Mittel/Groß)"],
                  ["Desktop- & Mobil-Screenshots", "8 Aufnahmen deiner Website als visuelle Dokumentation"],
                ].map(([title, desc]) => (
                  <div key={title} className="flex items-start gap-3 py-3 border-b border-gray-800/60">
                    <svg className="w-5 h-5 text-sky-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                    </svg>
                    <div>
                      <span className="font-medium">{title}</span>
                      <span className="text-gray-500 text-sm"> — {desc}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Bisherige Audits */}
            <div>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold">Bisherige Audits</h3>
                <button
                  onClick={fetchAuditReports}
                  className="text-sm text-gray-400 hover:text-sky-400 transition-colors flex items-center gap-2"
                >
                  <RefreshCw className={`h-4 w-4 ${isAuditLoading ? "animate-spin" : ""}`} />
                  Aktualisieren
                </button>
              </div>
              <div className="space-y-3">
                {isAuditLoading && auditReports.length === 0 ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="h-6 w-6 animate-spin text-sky-400 mr-3" />
                    <span className="text-gray-400">Lade Reports...</span>
                  </div>
                ) : auditReports.length === 0 ? (
                  <p className="text-gray-500 py-8">Noch keine Audits durchgeführt.</p>
                ) : (
                  auditReports.map((report) => (
                    <button
                      key={report.file}
                      onClick={() => window.open(`${SITEAUDIT_API}/ergebnisse/${report.file}`, "_blank")}
                      className="w-full flex items-center justify-between bg-gray-900 border border-gray-800 rounded-xl px-5 py-4 hover:border-sky-500/50 transition-colors group text-left"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-2 h-2 rounded-full ${
                          !report.score ? "bg-gray-600" :
                          (() => { const [n,d] = report.score.split("/").map(Number); const p = n/d;
                            return p >= 0.85 ? "bg-green-400" : p >= 0.65 ? "bg-yellow-400" : p >= 0.45 ? "bg-orange-400" : "bg-red-400";
                          })()
                        }`} />
                        <span className="font-medium group-hover:text-sky-400 transition-colors">{report.domain}</span>
                        <span className="text-gray-600 text-sm">{report.date}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        {report.score && (
                          <span className={`text-sm font-mono ${
                            (() => { const [n,d] = report.score.split("/").map(Number); const p = n/d;
                              return p >= 0.85 ? "text-green-400" : p >= 0.65 ? "text-yellow-400" : p >= 0.45 ? "text-orange-400" : "text-red-400";
                            })()
                          }`}>
                            {report.score} Punkte
                          </span>
                        )}
                        <span className="text-gray-600 group-hover:text-gray-400 transition-colors">&rarr;</span>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

      </div>
      </div>
    </>
  );
};

export default AdminDashboard;
